import { Component, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { HandleConsentSimplifiedComponent } from '../handle-consent-simplified/handle-consent-simplified.component';
import { ConsentRequest, NotificationStatus } from '@models/consent.model';
import { ConsentService } from '@services/consent.service';
import { UtilService } from '@services/util.service';

export interface ConsentDialogData {
  consentRequestId: string;
  title?: string;
}

@Component({
  selector: 'app-consent-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    HandleConsentSimplifiedComponent
  ],
  template: `
    <div class="consent-dialog">
      <div class="dialog-header" mat-dialog-title>
        <h2>{{ data.title || 'Consent Request' }}</h2>
        <button mat-icon-button (click)="close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <div class="dialog-content" mat-dialog-content>
        <app-handle-consent-simplified 
          [consentRequestId]="data.consentRequestId"
          (shareIncomplete)="onShareIncomplete($event)"
          (complete)="onConsentComplete($event)">
        </app-handle-consent-simplified>
      </div>
    </div>
  `,
  styles: [`
    .consent-dialog {
      width: 100%;
      height: 100%;
      // max-width: 800px;
      // height: 80vh;
      // max-height: 600px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    
    .dialog-header {
        display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid #e0e0e0;
      color: var(--mat-sys-primary);
      background: var(--mat-sys-secondary-fixed);
    }
    
    .dialog-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }
    
    .close-btn {
      margin-left: auto;
    }
    
    .dialog-content {
      flex: 1;
      overflow: hidden;
      padding: 0;
    }
    
    app-handle-consent-simplified {
      display: block;
      width: 100%;
      height: 100%;
    }

    /* Global dialog panel styles */
    :host ::ng-deep .consent-dialog-panel .mat-mdc-dialog-container {
      padding: 0;
      overflow: hidden;
    }
    
    :host ::ng-deep .consent-dialog-panel .mat-mdc-dialog-content {
      margin: 0;
      padding: 0;
      max-height: none;
    }
    
    :host ::ng-deep .consent-dialog-panel .mat-mdc-dialog-title {
      margin: 0;
      padding: 24px 24px 0 24px;
    }
  `]
})
export class ConsentDialogComponent {
  
  private dialogRef = inject(MatDialogRef<ConsentDialogComponent>);
  private consentService = inject(ConsentService);
  private utilService = inject(UtilService);
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ConsentDialogData
  ) {}
  
  close(): void {
    // Show confirmation popup
    this.utilService.openPopup(
      'Decline Consent Request',
      'The requestor will not be able to access your data. Are you sure you want to decline?',
      'YesOrNo'
    ).subscribe(confirmed => {
      if (confirmed) {
        // User confirmed decline - update notification status and close
        this.consentService.updateNotificationStatus(
          this.data.consentRequestId, 
          NotificationStatus.DISMISSED
        ).subscribe({
          next: (success) => {
            if (success) {
              console.log('Consent request dismissed successfully');
            }
            this.dialogRef.close();
          },
          error: (error) => {
            console.error('Error updating notification status:', error);
            // Close anyway even if the API call fails
            this.dialogRef.close();
          }
        });
      }
      // If not confirmed, don't close the dialog
    });
  }

  /**
   * Handle completion event from the consent component
   * Auto-close the dialog after a second delay
   */
  onConsentComplete({status, message}: {status: 'SUCCESS' | 'FAILURE', message: string}): void {
    // Close the dialog after 1 second delay to allow user to see the completion state
    setTimeout(() => {
      this.dialogRef.close({ status, message });
    }, 1000);
  }

  onShareIncomplete(shareDetails: {requestId: string | null, consentRequest: ConsentRequest}): void {
    setTimeout(() => {
      this.dialogRef.close({ status: 'SHARE_INCOMPLETE', message: shareDetails});
    }, 500);
  }
}
